import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./CoinFlipTransition.css";

// Paths where the coin portal is hidden and swipe flip is disabled.
// Add any utility / legal / auth pages here.
const isBlockedPath = (path) =>
  path.startsWith("/admin") ||
  path === "/privacy";

const isEditPath = (path) => (
  path === "/blog" ||
  path.startsWith("/blog/") ||
  path === "/podcast"
);

const getSideFromPath = (path) => (isEditPath(path) ? "edit" : "studio");
const canFlipFromPath = (path) => !isBlockedPath(path);

const fullPathFromLocation = (location) => (
  `${location.pathname}${location.search || ""}${location.hash || ""}`
);

const defaultTargetForSide = (side) => (side === "studio" ? "/" : "/blog");

const targetForOppositeSide = (from) => {
  const toSide = from === "studio" ? "edit" : "studio";
  return defaultTargetForSide(toSide);
};

export const startCoinFlip = (to, from) => {
  window.dispatchEvent(new CustomEvent("pp-coin-flip", { detail: { to, from } }));
};

const PARTICLES = [
  { px: "78px",  py: "0px",    delay: "0s",     size: 7, gold: true  },
  { px: "68px",  py: "39px",   delay: "0.025s", size: 5, gold: false },
  { px: "39px",  py: "68px",   delay: "0.04s",  size: 8, gold: true  },
  { px: "0px",   py: "78px",   delay: "0.01s",  size: 5, gold: false },
  { px: "-39px", py: "68px",   delay: "0.05s",  size: 7, gold: true  },
  { px: "-68px", py: "39px",   delay: "0.02s",  size: 6, gold: false },
  { px: "-78px", py: "0px",    delay: "0s",     size: 8, gold: true  },
  { px: "-68px", py: "-39px",  delay: "0.03s",  size: 5, gold: false },
  { px: "-39px", py: "-68px",  delay: "0.045s", size: 7, gold: true  },
  { px: "0px",   py: "-78px",  delay: "0.015s", size: 6, gold: false },
  { px: "39px",  py: "-68px",  delay: "0.035s", size: 8, gold: true  },
  { px: "68px",  py: "-39px",  delay: "0.02s",  size: 5, gold: false },
];

export default function CoinFlipTransition() {
  const navigate = useNavigate();
  const location = useLocation();
  const [flip, setFlip] = useState(null);
  const [drag, setDrag] = useState(null);
  const touchRef = useRef({ x: 0, y: 0, t: 0 });
  const lockRef = useRef(false);
  const pointerRef = useRef({ x: 0, y: 0, dx: 0, dy: 0, active: false });

  useEffect(() => {
    const run = (event) => {
      const from = event.detail?.from || getSideFromPath(location.pathname);
      const explicitTo = event.detail?.to;
      const to = explicitTo || targetForOppositeSide(from);
      if (!to || lockRef.current || to === fullPathFromLocation(location)) return;
      lockRef.current = true;
      setFlip({ to, from });
      window.setTimeout(() => navigate(to), 680);
      window.setTimeout(() => {
        setFlip(null);
        lockRef.current = false;
      }, 1180);
    };

    window.addEventListener("pp-coin-flip", run);
    return () => window.removeEventListener("pp-coin-flip", run);
  }, [location, navigate]);

  useEffect(() => {
    const onStart = (event) => {
      if (!canFlipFromPath(location.pathname) || event.touches.length !== 1 || event.target.closest?.("input, textarea, select, button, a")) return;
      const t = event.touches[0];
      touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    };

    const onEnd = (event) => {
      if (!canFlipFromPath(location.pathname) || lockRef.current) return;
      const start = touchRef.current;
      const t = event.changedTouches?.[0];
      if (!t || !start.t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      const fastEnough = Date.now() - start.t < 900;
      if (!fastEnough || Math.abs(dx) < 72 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      const side = getSideFromPath(location.pathname);
      if (side === "studio" && dx < 0) startCoinFlip(undefined, "studio");
      if (side === "edit" && dx > 0) startCoinFlip(undefined, "edit");
    };

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchend", onEnd);
    };
  }, [location.pathname]);

  const canShowPortal = canFlipFromPath(location.pathname);
  const idleFrom = getSideFromPath(location.pathname);
  const idleDestName = idleFrom === "studio" ? "The PP Edit" : "The PP Studio";

  const onPointerDown = (event) => {
    if (!canShowPortal || lockRef.current) return;
    pointerRef.current = { x: event.clientX, y: event.clientY, dx: 0, dy: 0, active: true };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDrag({ dx: 0, dy: 0 });
  };

  const onPointerMove = (event) => {
    if (!pointerRef.current.active) return;
    const dx = event.clientX - pointerRef.current.x;
    const dy = event.clientY - pointerRef.current.y;
    pointerRef.current.dx = dx;
    pointerRef.current.dy = dy;
    setDrag({ dx: Math.max(-120, Math.min(120, dx)), dy: Math.max(-28, Math.min(28, dy)) });
  };

  const onPointerUp = () => {
    if (!pointerRef.current.active) return;
    const { dx } = pointerRef.current;
    pointerRef.current.active = false;
    setDrag(null);
    if (idleFrom === "studio" && dx < -70) startCoinFlip(undefined, idleFrom);
    if (idleFrom === "edit" && dx > 70) startCoinFlip(undefined, idleFrom);
  };

  return (
    <>
      {canShowPortal && !flip && (
        <button
          type="button"
          className={`coin-idle-portal side-${idleFrom}${drag ? " dragging" : ""}`}
          style={drag ? { "--drag-x": `${drag.dx}px`, "--drag-y": `${drag.dy}px` } : undefined}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onClick={(event) => {
            if (Math.abs(pointerRef.current.dx) > 8 || Math.abs(pointerRef.current.dy) > 8) {
              event.preventDefault();
              return;
            }
            startCoinFlip(undefined, idleFrom);
          }}
          aria-label={idleFrom === "studio" ? "Flip to The PP Edit" : "Flip to The PP Studio"}
        >
          <span className="coin-idle-disc" aria-hidden="true">
            <span className="coin-idle-pp">PP</span>
          </span>
          <span className="coin-idle-dest">{idleDestName}</span>
          <span className="coin-idle-track" aria-hidden="true">
            <i /><i /><i />
          </span>
        </button>
      )}

      {flip && (
        <div className={`coin-flip-stage from-${flip.from}`} aria-hidden="true">
          <div className="coin-flip-glow" />

          <div className="coin-flip-shadow" />

          <div className="coin-flip-orbit">
            <div className="coin-flip-disc">
              <div className="coin-face coin-face-front">
                <div className="coin-face-label">
                  <span>The PP</span>
                  <small>Studio</small>
                </div>
                <i />
              </div>
              <div className="coin-face coin-face-back">
                <div className="coin-face-label">
                  <span>The PP</span>
                  <small>Edit</small>
                </div>
                <i />
              </div>
            </div>
          </div>

          <div className="coin-flip-rings">
            <div className="coin-flip-ring" />
            <div className="coin-flip-ring" />
            <div className="coin-flip-ring" />
          </div>

          <div className="coin-flip-particles">
            {PARTICLES.map((p, i) => (
              <div
                key={i}
                className={`coin-particle${p.gold ? " gold" : ""}`}
                style={{ "--px": p.px, "--py": p.py, "--delay": p.delay, "--sz": `${p.size}px` }}
              />
            ))}
          </div>

          <div className="coin-flip-edge-flash" />

          <div className="coin-flip-title">
            <span className="cft-pp">{flip.from === "studio" ? "The PP Edit" : "The PP Studio"}</span>
          </div>

          <div className="coin-flip-ripple r1" />
          <div className="coin-flip-ripple r2" />
        </div>
      )}
    </>
  );
}
